import './Im.sass';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute, useRouter } from 'react-router5';

import {
  ActionBottomSheet,
  Header,
  InlineHeader,
  List,
  ListItem,
  LottieIcon,
  Placeholder,
  PullToRefresh,
  ScrollView,
  Tabs,
  TabsItem,
} from '$uikit';
import { Loader, MetaTags, NotifyRequest, UserCell } from '$shared/components';
import {
  imActions,
  imAsPeerIdSelector,
  imListsSelector,
  imSelector,
  imServiceAccountsSelector,
  unAnsweredCountSelector,
} from '$store/im';
import {
  IcBot28,
  IcBubble24,
  IcChart28,
  IcMessage28,
  IcMore28,
  IcOther28,
  IcSend28,
  IcStar28,
  IcTelegram16,
  IcTelegram24,
  IcWrite28,
} from '$assets';
import { DialogModel } from '$store/models';
import { Dialog } from '$core/Im/Dialog/Dialog';
import { useCurrentUserInfo, useImActions, useTranslator } from '$hooks';
import { goTo, showModal } from '$navigation/helper';
import { BROADCAST, BROADCAST_STATS, IM, WELCOME_MESSAGE } from '$shared/constants/pages';
import { contentPrioritySelector, mainSelector } from '$store/main';
import { ContentPriorityType } from '$store/main/interface';
import { statReachGoal } from '$utils';
import { WelcomeMessageType } from '$store/im/interface';
import { UserModel } from '$store/models';

enum ImActionsGroups {
  broadcast,
  welcomeMessage,
}

enum ImActionsValues {
  newBroadcast = 'newBroadcast',
  broadcastStats = 'broadcastStats',
  welcomeMessageSponsor = 'welcomeMessageSponsor',
  welcomeMessageTgSubscriber = 'welcomeMessageTgSubscriber',
  welcomeMessageFollower = 'welcomeMessageFollower',
}

export default function Im() {
  const {
    isDialogsLoading,
    dialogs,
    dialogsList,
    history,
    isDialogsLoadingMore,
    nextFrom,
    suggestions,
    hasWelcomeMessage,
  } = useSelector(imSelector);
  const dispatch = useDispatch();
  const im = useImActions();
  const { canBroadcastMessages, mVerified, hasBot, users } = useSelector(mainSelector);
  const t = useTranslator();
  const user = useCurrentUserInfo();
  const router = useRouter();
  const { route } = useRoute();
  const [showActions, setShowActions] = useState(false);
  const activeTab = route.params.section || 'all';
  const contentPriority = useSelector(contentPrioritySelector);
  const isAdultUser =
    user.verified !== 2 && contentPriority === ContentPriorityType.ADULT;
  const unAnsweredCount = useSelector(unAnsweredCountSelector);
  const lists = useSelector(imListsSelector);
  const serviceAccounts = useSelector(imServiceAccountsSelector);
  const asPeerId = useSelector(imAsPeerIdSelector);

  useEffect(() => {
    im.loadDialogs();
  }, []);

  useEffect(() => {
    if (activeTab === 'all' && activeTab !== 'suggestions') {
      im.loadDialogs();
    }
  }, [activeTab]);

  const dialogsPrepared = useMemo(() => {
    const pinned: DialogModel[] = [];
    const rest: DialogModel[] = [];
    const seen: Record<number, boolean> = {};

    for (const id of dialogsList) {
      if (seen[id]) continue;
      seen[id] = true;
      const d = dialogs[id];
      if (!d) continue;

      if (users[d.peerId]?.isService) {
        pinned.push(d);
      } else {
        rest.push(d);
      }
    }

    if (pinned.length === 0) return rest;

    pinned.push(...rest);
    return pinned;
  }, [dialogs, dialogsList, users]);
  // const dialogsPrepared = useMemo(() => {
  //   return Object.values(dialogs).sort((a: DialogModel, b: DialogModel) => {
  //     if (user.verified === 2) {
  //       if (a.modelLastMsgId > b.modelLastMsgId) {
  //         return -1;
  //       } else if (a.modelLastMsgId < b.modelLastMsgId) {
  //         return 1;
  //       }
  //     }
  //
  //     const aMsg = (history[a.peerId] || {})[a.lastMsgId];
  //     const bMsg = (history[b.peerId] || {})[b.lastMsgId];
  //
  //     const aTs = aMsg?.createdAt ?? a.createdAt;
  //     const bTs = bMsg?.createdAt ?? b.createdAt;
  //
  //     return aTs > bTs ? -1 : 1;
  //   });
  // }, [dialogs, history, user]);

  const handleWrite = useWriteMessageCallback();

  const handleOpenTelegramBot = useCallback(() => {
    dispatch(imActions.setTelegramSheetVisible(true));
  }, []);

  const handleWelcomeMessage = useCallback((type = WelcomeMessageType.FOLLOWER) => {
    goTo(WELCOME_MESSAGE, { type });
    statReachGoal('welcome_message_btn_press', {
      type,
    });
    statReachGoal(`welcome_message_${type}_btn_press`);
  }, []);

  const fastActions = useMemo(() => {
    if (asPeerId) {
      return null;
    }

    if (isDialogsLoading && !Object.values(dialogs).length) {
      return null;
    }

    const actions: any = [];
    if (user.verified === 2) {
      actions.push(
        <ListItem icon={<IcWrite28 />} onClick={handleWrite}>
          {t('im_new_broadcast')}
        </ListItem>,
      );
    }

    if (hasBot) {
      actions.push(
        <ListItem icon={<IcBot28 />} onClick={handleOpenTelegramBot}>
          {t('im_telegram_bot')}
        </ListItem>,
      );
    }

    if (user.verified === 2 && !hasWelcomeMessage) {
      actions.push(
        <ListItem icon={<IcMessage28 />} onClick={() => handleWelcomeMessage()}>
          {t('im_setup_welcome_message')}
        </ListItem>,
      );
    }

    if (!actions.length) {
      return null;
    }

    return actions;
  }, [t, isDialogsLoading, user, hasBot, hasWelcomeMessage, dialogs, asPeerId]);

  const handleServiceAccountChange = useCallback(
    (accountId: number) => {
      router.navigate(IM, {}, { replace: true });
      if (accountId === 0) {
        dispatch(imActions.resetAsPeerId());
      } else {
        dispatch(imActions.setAsPeerId(accountId));
      }
    },
    [dispatch, router],
  );

  const handleTabChange = useCallback((newTab: string) => {
    const params: any = {};
    if (newTab !== 'all') {
      params.section = newTab;
    }
    router.navigate(IM, params, { replace: true });
    im.loadDialogs();

    const scrollNode = document.querySelector('._ScrollView');
    if (scrollNode) {
      scrollNode.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleClickMore = useCallback(() => {
    goTo(BROADCAST_STATS);
  }, []);

  const handleActionSheetChange = (value: string) => {
    if (value === ImActionsValues.newBroadcast) {
      handleWrite();
    } else if (value === ImActionsValues.broadcastStats) {
      handleClickMore();
    } else if (value === ImActionsValues.welcomeMessageSponsor) {
      handleWelcomeMessage(WelcomeMessageType.SPONSOR);
    } else if (value === ImActionsValues.welcomeMessageTgSubscriber) {
      handleWelcomeMessage(WelcomeMessageType.TG_SUBSCRIBER);
    } else if (value === ImActionsValues.welcomeMessageFollower) {
      handleWelcomeMessage(WelcomeMessageType.FOLLOWER);
    }
  };

  function renderContent() {
    if (isDialogsLoading && !dialogsPrepared.length) {
      return <Loader isWrapped />;
    }

    if (activeTab === 'suggestions') {
      if (!suggestions.length) {
        return <Placeholder centered>{t('im_suggestions_empty')}</Placeholder>;
      }

      return (
        <>
          <div className="Im_suggestions_caption">{t('im_tab_suggestions_caption')}</div>
          {suggestions.map((item) => (
            <UserCell objectId={item} key={item} isFollowBtnShown />
          ))}
        </>
      );
    }

    if (!dialogsPrepared.length) {
      return (
        <Placeholder iconSize={120} icon={<LottieIcon name="messages" />}>
          {t('im_empty')}
        </Placeholder>
      );
    }

    return (
      <div className="Im">
        <NotifyRequest />

        {dialogsPrepared.map((dialog) => (
          <Dialog key={dialog.peerId} dialog={dialog} />
        ))}
      </div>
    );
  }

  return (
    <>
      <MetaTags title={t('im_title')} />
      <Header
        rightIcons={[
          user.verified && !asPeerId
            ? {
                icon: <IcMore28 />,
                onClick: () => setShowActions(true),
              }
            : {
                icon: <IcWrite28 />,
                onClick: () => showModal('write'),
              },
        ]}
      >
        {t('im_title')}
      </Header>
      <ScrollView
        onLoadMore={() => im.loadMoreDialogs(nextFrom)}
        canLoadMore={
          !isDialogsLoading &&
          !isDialogsLoadingMore &&
          !!nextFrom &&
          activeTab !== 'suggestions'
        }
        isLoadingMore={isDialogsLoadingMore}
      >
        {serviceAccounts.length > 0 && (
          <Tabs selected={String(asPeerId)} onChange={(v) => handleServiceAccountChange(+v)}>
            <TabsItem value="0">My Chats</TabsItem>
            {serviceAccounts.map((id) => {
              const account = users[id] as UserModel | undefined;
              return (
                <TabsItem key={id} value={String(id)}>
                  {account ? `${account.firstName} ${account.lastName}` : `#${id}`}
                </TabsItem>
              );
            })}
          </Tabs>
        )}
        {user.verified === 2 && asPeerId > 0 && (
          <Tabs selected={activeTab} onChange={handleTabChange}>
            <TabsItem value="all">{t('im_tab_all')}</TabsItem>
            <TabsItem value="adult">Adult</TabsItem>
            <TabsItem value="model">Model</TabsItem>
            <TabsItem value="simple">Simple</TabsItem>
          </Tabs>
        )}
        {user.verified === 2 && !asPeerId && (
          <Tabs selected={activeTab} onChange={handleTabChange}>
            <TabsItem value="all">{t('im_tab_all')}</TabsItem>
            <TabsItem value="unread">{t('im_tab_unread')}</TabsItem>
            {lists.map((list) => (
              <TabsItem key={list.listId} value={list.listId}>
                {list.title}
              </TabsItem>
            ))}
            <TabsItem value="not_answered" counter={unAnsweredCount}>
              {t('im_tab_not_answered')}
            </TabsItem>
            <TabsItem value="online">{t('im_tab_online')}</TabsItem>
            <TabsItem value="telegram">Telegram</TabsItem>
            <TabsItem value="sponsors">{t('im_tab_sponsors')}</TabsItem>
            <TabsItem value="paid">{t('im_tab_paid')}</TabsItem>
            <TabsItem value="tips">{t('im_tab_tips')}</TabsItem>
          </Tabs>
        )}
        {isAdultUser && (
          <Tabs selected={activeTab} onChange={handleTabChange}>
            <TabsItem value="all">{t('im_tab_all')}</TabsItem>
            <TabsItem value="online">{t('im_tab_online')}</TabsItem>
            {suggestions.length > 0 && (
              <TabsItem value="suggestions">{t('im_tab_suggestions')}</TabsItem>
            )}
          </Tabs>
        )}
        {fastActions && (
          <div className="Im__creator_actions">
            <List>{fastActions}</List>
          </div>
        )}
        <PullToRefresh
          isRefreshing={isDialogsLoading}
          onRefresh={() => im.loadDialogs()}
        >
          {renderContent()}
          {!nextFrom &&
            isAdultUser &&
            activeTab !== 'suggestions' &&
            suggestions.length > 0 && (
              <>
                <InlineHeader caption={t('im_tab_suggestions_caption')}>
                  {t('im_tab_suggestions')}
                </InlineHeader>
                {suggestions.map((item) => (
                  <UserCell objectId={item} key={item} isFollowBtnShown />
                ))}
              </>
            )}
        </PullToRefresh>
      </ScrollView>
      <ActionBottomSheet
        title={t('im_bottom_sheet_title')}
        isOpened={showActions}
        onClose={() => setShowActions(false)}
        onChange={handleActionSheetChange}
        groups={[
          { id: ImActionsGroups.welcomeMessage, label: t('im_bottom_sheet_wm_group') },
        ]}
        options={[
          {
            groupId: ImActionsGroups.broadcast,
            icon: <IcWrite28 />,
            label: t('im_bottom_sheet_new_broadcast'),
            value: ImActionsValues.newBroadcast,
          },
          {
            groupId: ImActionsGroups.broadcast,
            icon: <IcChart28 />,
            label: t('im_bottom_sheet_broadcast_stats'),
            value: ImActionsValues.broadcastStats,
          },
          {
            groupId: ImActionsGroups.welcomeMessage,
            icon: <IcBubble24 />,
            label: t('im_bottom_sheet_wm_follower'),
            value: ImActionsValues.welcomeMessageFollower,
          },
          {
            groupId: ImActionsGroups.welcomeMessage,
            icon: <IcStar28 />,
            label: t('im_bottom_sheet_wm_sponsor'),
            value: ImActionsValues.welcomeMessageSponsor,
          },
          {
            groupId: ImActionsGroups.welcomeMessage,
            icon: <IcTelegram24 />,
            label: t('im_bottom_sheet_wm_tg_subscriber'),
            value: ImActionsValues.welcomeMessageTgSubscriber,
          },
        ]}
      />
    </>
  );
}

export const useWriteMessageCallback = () => {
  const { canBroadcastMessages, mVerified } = useSelector(mainSelector);

  return useCallback(() => {
    if (canBroadcastMessages && mVerified) {
      statReachGoal('broadcast_btn_press');
      goTo(BROADCAST);
    } else {
      showModal('write');
    }
  }, [canBroadcastMessages, mVerified]);
};
